import { Component, inject, signal } from '@angular/core';
import { TabComponent } from '../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../components/tab-layout/tab-layout.component';
import { APITableComponent } from '../../../components/table/api-table/api-table.component';
import { TableRow } from '../../../components/table/table.interface';
import { ModalComponent } from '../../../components/modal/modal.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ConfigService } from '../../../services/config.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { CommonModule } from '@angular/common';
import { LoadingSpinnerComponent } from '../../../components/loading-spinner/loading-spinner.component';

interface Activity {
  from: string;
  to: string;
  leaveType: string;
  duration: number;
  status: string;
  subCategory?: string;
}

interface ActivitiesResponse {
  success: boolean;
  message: string;
  messageCode: string;
  data: {
    activities: Activity[];
  };
}

interface OverviewRowValue {
  userId: string;
  Name: string;
  Category?: string;
  Balance?: string;
  'Emergencies Balance'?: string;
}

@Component({
  selector: 'app-overview',
  imports: [
    TabLayoutComponent,
    APITableComponent,
    ModalComponent,
    CommonModule,
    LoadingSpinnerComponent,
  ],
  providers: [{ provide: TabComponent, useExisting: OverviewComponent }],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
})
export class OverviewComponent extends TabComponent {
  override name: string = this.dictionaryService.get('Overview');

  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private snackbarService = inject(SnackbarService);

  isModalOpen = signal<boolean>(false);
  isModalLoading = signal<boolean>(false);
  modalTitle = '';
  activities: Activity[] = [];

  public async main(): Promise<void> {}

  onRowClick = (row: TableRow) => {
    const rowValue = row.value as unknown as OverviewRowValue;
    const name = rowValue.Name;
    const userId = rowValue.userId;

    if (!userId?.trim()) {
      this.snackbarService.showError(
        this.dictionaryService.get('UnableToOpenUserLeavesOverview'),
      );
      return;
    }

    this.modalTitle = name || this.dictionaryService.get('Activities');
    this.activities = [];
    this.isModalLoading.set(true);
    this.isModalOpen.set(true);

    const { from, to } = this.configService.getApiDateRange();
    const baseUrl = environment.apiBaseUrl;

    this.http
      .get<ActivitiesResponse>(
        `${baseUrl}/activities/${userId}/leaves-overview?from=${from}&to=${to}`,
      )
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.activities = res.data.activities;
          }
          this.isModalLoading.set(false);
        },
        error: (err) => {
          console.error('Error fetching activities:', err);
          this.snackbarService.showError(
            this.dictionaryService.get('ErrorFetchingActivities'),
          );
          this.isModalLoading.set(false);
        },
      });
  };

  onModalClose = () => {
    this.isModalOpen.set(false);
    this.activities = [];
  };

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'accepted':
        return 'status-accepted';
      case 'rejected':
        return 'status-rejected';
      case 'pending':
        return 'status-pending';
      default:
        return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status?.toLowerCase()) {
      case 'accepted':
        return this.dictionaryService.get('Accepted');
      case 'rejected':
        return this.dictionaryService.get('Rejected');
      case 'pending':
        return this.dictionaryService.get('Pending');
      default:
        return status;
    }
  }
}
